/**
 * SUPPLIES (docs/01 §8).
 *
 * The shopping list is not a separate list — it is every item that is not `ok`,
 * which is why there is nothing to keep in sync and nothing to tick off twice.
 *
 * Because restocks are timestamped, the cadence is free: "coffee usually lasts
 * 18 days, it has been 16". That hint is AMBER when an item is due, never red —
 * running low on coffee is not an error.
 */

import {
  SpaceEmpty,
  SpaceStyles,
  daysSince,
  formatAgo,
  mutationCtx,
  restockCadenceDays,
  useAction,
  useNow,
  useSpace,
  type SpaceScreenProps,
  type SupplyEventRow,
} from './spaceData.js'
import { mutations } from '../../lib/mutations.js'
import type { SupplyItem, SupplyState } from '@office/domain'

export interface SuppliesProps extends SpaceScreenProps {
  readonly tickMs?: number
}

export function Supplies(props: SuppliesProps) {
  const { db, clock } = props
  const now = useNow(clock, props.tickMs ?? 60_000)
  const space = useSpace(db)
  const run = useAction()

  const active = space.supplies.filter(i => i.active)
  const shopping = active.filter(i => i.state !== 'ok')
  const stocked = active.filter(i => i.state === 'ok')

  const flag = (item: SupplyItem, state: SupplyState) =>
    run(() => mutations.flagSupply(mutationCtx(props), String(item.id), state))

  return (
    <section className="sp" aria-labelledby="sp-supplies-h">
      <SpaceStyles />
      <div className="sp-head">
        <h2 className="sp-title" id="sp-supplies-h">
          Supplies
        </h2>
        <span className="sp-note">
          {shopping.length === 0 ? 'Nothing to buy' : `${shopping.length} to buy`}
        </span>
      </div>

      <div className="card">
        <h3 className="sp-card-title">
          Shopping list
        </h3>
        {shopping.length === 0 ? (
          <SpaceEmpty hint="The shopping list is simply everything that is not OK, so there is no second list to keep in step.">
            Everything is stocked.
          </SpaceEmpty>
        ) : (
          <ul className="sp-list sp-rows" aria-label="Shopping list">
            {shopping.map(item => (
              <li key={item.id} className="sp-row">
                <span className="grow">
                  <span className="sp-person-name">{item.name}</span>
                  <span className="sp-person-meta">
                    {item.category}
                    {item.lastFlaggedAt === null
                      ? ''
                      : ` · flagged ${formatAgo(item.lastFlaggedAt, now)}`}
                  </span>
                </span>
                <span className="pill pill-warn">{item.state === 'out' ? 'Out' : 'Low'}</span>
                {/* `low` -> `out` has to be reachable: running out is what
                    actually happens next to something flagged low, and without
                    this the shopping list can only ever be un-flagged. */}
                {item.state === 'low' ? (
                  <button
                    type="button"
                    className="btn"
                    aria-label={`Flag ${item.name} out`}
                    onClick={() => flag(item, 'out')}
                  >
                    Out
                  </button>
                ) : null}
                <button
                  type="button"
                  className="btn"
                  aria-label={`Mark ${item.name} restocked`}
                  onClick={() => flag(item, 'ok')}
                >
                  Restocked
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="card">
        <h3 className="sp-card-title">
          Stocked
        </h3>
        {stocked.length === 0 ? (
          <SpaceEmpty mark="📦" hint="Add items as you buy them: once there are two restocks to measure between, the cadence hint appears on its own.">
            Nothing stocked.
          </SpaceEmpty>
        ) : (
          <ul className="sp-list sp-rows" aria-label="Stocked supplies">
            {stocked.map(item => (
              <li key={item.id} className="sp-row">
                <span className="grow">
                  <span className="sp-person-name">{item.name}</span>
                  <span className="sp-person-meta">
                    {cadenceLine(item, space.supplyEvents, now)}
                  </span>
                </span>
                <button
                  type="button"
                  className="btn"
                  aria-label={`Flag ${item.name} low`}
                  onClick={() => flag(item, 'low')}
                >
                  Low
                </button>
                <button
                  type="button"
                  className="btn"
                  aria-label={`Flag ${item.name} out`}
                  onClick={() => flag(item, 'out')}
                >
                  Out
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </section>
  )
}

/**
 * "usually lasts 18 days · 16 days since restock". Silent when there are fewer
 * than two restocks to measure between — a cadence from one data point is a
 * guess dressed up as a fact.
 */
export function cadenceLine(
  item: SupplyItem,
  events: readonly SupplyEventRow[],
  now: number,
): string {
  const since = daysSince(item.lastRestockedAt, now)
  const cadence = restockCadenceDays(events, String(item.id))
  if (cadence === null) {
    return since === null ? item.category : `${item.category} · restocked ${since}d ago`
  }
  const sinceText = since === null ? '' : ` · ${since}d since restock`
  return `Usually lasts ${cadence}d${sinceText}`
}
